import { cva, VariantProps } from 'class-variance-authority';
import classNamesStyle from 'classnames';
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../utils/cn';

const inputVariants = cva('focus:outline-none', {
  variants: {
    size: {
      xs: 'h-[26px] py-1 px-1.5 text-sm',
      sm: 'h-[32px] px-4 py-2 text-sm',
      md: 'h-[39px] py-2.5 px-4 text-sm',
      lg: 'h-[48px] py-3.5 px-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> &
  VariantProps<typeof inputVariants> & {
    error?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      size,
      error,
      className,
      endIcon,
      startIcon,
      startAdornment,
      endAdornment,
      value,
      ...props
    },
    ref,
  ) => (
    <div
      className={classNamesStyle(
        'relative flex items-center min-w-0 border border-gray-300 rounded-lg focus-within:!ring-[3px]',
        {
          'focus-within:!ring-red-200  focus-within:!border-red-500 !border-red-500 text-red-700':
            error,
          'focus-within:ring-gray-400/25 focus-within:!border-gray-950': !error,
        },
        className,
      )}
    >
      {startAdornment && (
        <div
          className={classNamesStyle(
            cn(inputVariants({ size })),
            'flex items-center justify-center !h-auto border-r text-gray-500',
          )}
        >
          {startAdornment}
        </div>
      )}

      {startIcon && (
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">{startIcon}</div>
      )}
      <input
        type={type}
        data-1p-ignore="true"
        className={classNamesStyle(
          cn(inputVariants({ size })),
          {
            '!pl-9': !!startIcon,
            '!pr-12': !!endIcon,
            'rounded-l-none border-x-0': startAdornment,
            'rounded-r-none border-x-0': endAdornment,
          },
          ' disable-autofill w-full min-w-0 focus:outline-none rounded-[inherit] placeholder-gray-500  border-none disabled:bg-gray-50 disabled:text-gray-400',
        )}
        ref={ref}
        onKeyDown={
          type === 'number'
            ? (e) => {
                if (e.key === '.' || e.key === 'e') {
                  e.preventDefault();
                }
              }
            : undefined
        }
        {...(value != null ? { value } : {})}
        {...props}
      />
      {endIcon && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">{endIcon}</div>
      )}
      {endAdornment && (
        <div
          className={classNamesStyle(
            cn(inputVariants({ size })),
            'flex items-center justify-center h-[21px] border-l text-gray-500',
          )}
        >
          {endAdornment}
        </div>
      )}
    </div>
  ),
);
